/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { B2B } from '@/components/blocks/B2B';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { LoginChangePasswordForm } from '@/components/content/Login/parts/LoginChangePasswordForm';
import { LoginForm } from '@/components/content/Login/parts/LoginForm';
import { LoginRegistrationB2BForm } from '@/components/content/Login/parts/LoginRegistrationB2BForm';
import { Recaptcha } from '@/components/content/Login/parts/Recaptcha';
import {
	espotContainerSX,
	loginContainerDrawerSX,
	loginContainerSX,
	loginFormContainerDrawerSX,
	loginFormContainerSX,
} from '@/components/content/Login/styles/container';
import { useCartSWRKey } from '@/data/Content/Cart';
import { UserLogon, personMutatorKeyMatcher, useLogin } from '@/data/Content/Login';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { SessionDialogContext } from '@/data/context/sessionDialogCtx';
import { ValidateLocaleRequestContext } from '@/data/context/validateLocaleRequest';
import { ID } from '@/data/types/Basic';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { mergeSx } from '@/utils/mergeSx';
import { cartMutatorKeyMatcher } from '@/utils/mutatorKeyMatchers';
import { Box, Grid, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useContext } from 'react';
import { CHECKOUT } from 'shared/custom/constants/commonConstants';
import { useSWRConfig } from 'swr';

type Props = {
	closeDrawer?: () => void;
	id: ID;
	isDialogView?: boolean;
	drawerVersion?: boolean;
	onGoToForgotPassword?: () => void;
};

export const Login: FC<Props> = ({
	isDialogView = false,
	drawerVersion = false,
	closeDrawer,
	onGoToForgotPassword,
}) => {
	const theme = useTheme();
	const router = useNextRouter();
	const RouteLocal = useLocalization('Routes');
	const { mutate } = useSWRConfig();
	const currentCartSWRKey = useCartSWRKey(); // in current language

	const { loginSubmit, setPasswordExpired, passwordExpired } = useLogin();
	const { setValidateRequested } = useContext(ValidateLocaleRequestContext);
	const { setSessionDialogOpen } = useContext(SessionDialogContext);

	const orderConfirmationRoute = RouteLocal.OrderConfirmation.route.t();
	const forgotPasswordRoute = RouteLocal.ForgotPassword.route.t();

	const formattedPath = router.asPath.split('?')[0].replace(/\/$/, '');
	const isThankYouPage = formattedPath.endsWith(`/${orderConfirmationRoute}`);
	const isForgotPasswordPage = formattedPath.endsWith(`/${forgotPasswordRoute}`);

	const handleLogin = async (props: UserLogon): Promise<boolean | void> => {
		const resp = await loginSubmit({ ...props, isDialogView });
		if ('userId' in resp) {
			if (router.query.flow === CHECKOUT) {
				await router.push(RouteLocal.CheckOut.route.t());
			} else if (isThankYouPage) {
				// On the thank-you page the guest order session has already been logged out, so
				// staying here after authenticating would show stale/invalid order context.
				if (drawerVersion && closeDrawer) {
					closeDrawer();
				}
				await router.push('/');
			} else if (isForgotPasswordPage) {
				closeDrawer?.();
				await router.push('/');
			} else if (!isDialogView) {
				await router.push('/');
			} else if (drawerVersion && closeDrawer) {
				closeDrawer();
			}
			if (props.rememberMe) {
				resp['rememberMe'] = props.rememberMe;
			}
			logEventWithValues('SignIn', resp);
			setValidateRequested(true);
			setSessionDialogOpen(false);
			await mutate(personMutatorKeyMatcher(''));
			await mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
			await mutate(cartMutatorKeyMatcher('')); // at current page
			await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined); // all cart except current cart, e.g different locale
		} else {
			return false;
		}
	};

	const onCancel = () => {
		setPasswordExpired(null);
	};

	return (
		<>
			<Recaptcha />
			<Paper sx={mergeSx(loginContainerSX, drawerVersion ? loginContainerDrawerSX : {})}>
				{passwordExpired !== null ? (
					<Box
						sx={mergeSx(
							loginFormContainerSX(theme, isDialogView),
							drawerVersion ? loginFormContainerDrawerSX() : {}
						)}
					>
						<LoginChangePasswordForm
							onSubmit={handleLogin}
							onCancel={onCancel}
							passwordExpired={passwordExpired}
						/>
					</Box>
				) : (
					<Box
						sx={mergeSx(
							loginFormContainerSX(theme, isDialogView),
							drawerVersion ? loginFormContainerDrawerSX() : {}
						)}
					>
						<LoginForm
							onSubmit={handleLogin}
							isDialogView={isDialogView}
							drawerVersion={drawerVersion}
							closeDrawer={closeDrawer}
							onGoToForgotPassword={onGoToForgotPassword}
						/>
					</Box>
				)}
				{!isDialogView ? (
					<Box sx={espotContainerSX}>
						<EMarketingSpot
							id={'Login_AdditionalInfo'}
							properties={{ emsName: 'Login_AdditionalInfo' }}
						/>
					</Box>
				) : null}
			</Paper>
			<B2B>
				<Grid item xs={12} sm={8} md={6}>
					<LoginRegistrationB2BForm />
				</Grid>
			</B2B>
		</>
	);
};
